import KEmitter from '../../core/mixins/emitter/KEmitter'
import { getPropByPath, convertPropClass2ClassObjectLike } from '../../core/utils/KProps'
import _ from 'lodash'
import AsyncValidator from 'async-validator'

AsyncValidator.warning = _.noop

const VALID = 'valid'
const INVALID = 'invalid'
const PENDING = 'pending'

export default {
  name: 'KFormItem',
  componentName: 'KFormItem',
  mixins: [KEmitter],
  inject: ['kForm'],
  props: {
    label: {
      type: String,
      default: '',
    },
    prop: String,
    required: {
      type: Boolean,
      default: false,
    },
    showError: {
      type: Boolean,
      default: true,
    },
    rules: [Object, Array],
    hideLabel: Boolean,
    contentClass: {
      validator: (value) => _.isString(value) || _.isArray(value) || _.isObjectLike(value),
      default() {
        return {}
      },
    },
  },
  data() {
    return {
      initialValue: '',
      status: '',
      error: '',
      disabled: false,
      pristine: true,
      dirty: false,
      touched: false,
      untouched: true,
    }
  },
  watch: {
    touched(val) {
      this.untouched = !val
      // touched
      this.$emit('touched')
    },
    changed(val) {
      this.form.checkChange(val)
    },
  },
  computed: {
    context() {
      return this.kForm || {}
    },
    form() {
      let parent = this.$parent
      let parentName = parent.$options.componentName
      while (parentName !== 'KForm' && parent) {
        parent = parent.$parent
        parentName = parent.$options.componentName
      }
      return parent
    },
    fieldValue() {
      const model = this.form.model
      if (model && this.prop) {
        return getPropByPath(model, this.prop, true).value
      }
    },
    changed() {
      if (this.initialValue !== this.fieldValue) {
        return true
      }
    },
  },
  mounted() {
    this.dispatch('KForm', 'k.form.addField', [this])
    let initialValue = this.fieldValue
    if (Array.isArray(initialValue)) {
      initialValue = _.clone(initialValue)
    }
    this.initialValue = initialValue
    if (this.getRules().length) {
      this.$on('k.form.blur', () => {
        this.validate('blur')
        this.markAsTouched()
      })
      this.$on('k.form.change', () => {
        this.markAsDirty()
        if (this.disabled) {
          this.disabled = false
          return
        }
        this.validate('change')
      })
    }
  },
  render() {
    const formClass = {
      'km-form-item': true,
      'km-form-item--required': this.required,
      'is-invalid': this.showError && this.status === INVALID,
      'is-valid': this.status === VALID,
      'is-pristine': this.pristine,
      'is-dirty': this.dirty,
      'is-untouched': this.untouched,
      'is-touched': this.touched,
      'is-changed': this.changed,
    }

    const style = {}

    const data = {
      class: formClass,
      style,
    }

    const label = this.renderLabel()

    return (
      <div {...data}>
        {label}
        <div
          class={{
            'km-form-item__content': true,
            ...convertPropClass2ClassObjectLike(this.contentClass),
          }}
        >
          {this.$slots.default}
          {
            this.status === INVALID && this.showError
              ? <div class="km-form-item__invalid">
                {this.error}
              </div> : ''
          }
        </div>
      </div>
    )
  },
  methods: {
    renderLabel() {
      const label = this.$slots.label || this.label
      if (!label) return
      const width = this.context.labelWidth
      const justify = this.context.labelJustify
      const hide = this.hideLabel

      const itemClass = {
        'km-form-item__label': true,
        'is-hidden': hide,
        [`is-justify-${justify}`]: !!justify,
      }

      const style = {
        width,
      }

      const data = {
        class: itemClass,
        style,
      }

      return <div {...data}>{label}</div>
    },
    validate(trigger, callback = _.noop) {
      this.disabled = false
      const rules = this.getFilteredRule(trigger)
      if (!rules || rules.length === 0) {
        callback()
        return
      }
      this.status = PENDING
      const descriptor = {}
      descriptor[this.prop] = rules
      const validator = new AsyncValidator(descriptor)
      const model = {}
      if (_.some(rules, r => r.type === 'number')) {
        model[this.prop] = _.trim(this.fieldValue) === '' ? this.fieldValue : +this.fieldValue
      } else {
        model[this.prop] = this.fieldValue
      }
      validator.validate(model, { firstFields: true }, (errors, invalidFields) => {
        this.status = !errors ? VALID : INVALID
        this.error = errors ? errors[0].message : ''
        callback(this.error, invalidFields)
      })
    },
    getRules() {
      if (!this.prop) return []
      let formRules = this.form.rules
      const selfRules = this.rules
      const prop = getPropByPath(formRules, this.prop)
      formRules = formRules ? (prop.obj[this.prop] || prop.value) : []
      return [].concat(selfRules || formRules || [])
    },
    getFilteredRule(trigger) {
      let rules = this.getRules()
      if (!trigger) return rules
      rules = _.filter(rules, rule => {
        if (!rule.trigger) return true
        if (_.isArray(rule.trigger)) {
          return rule.trigger.indexOf(trigger) > -1
        } else {
          return rule.trigger === trigger
        }
      })
      return rules
    },
    clearValidate() {
      this.status = this.error = ''
      this.disabled = false
    },
    reset() {
      this.status = this.error = ''
      this.markAsPristine()
      this.markAsUntouched()
      let model = this.form.model
      let value = this.fieldValue
      let path = this.prop
      let prop = getPropByPath(model, path, true)
      this.disabled = true
      if (Array.isArray(value)) {
        prop.obj[prop.key] = [].concat(this.initialValue)
      } else {
        prop.obj[prop.key] = this.initialValue
      }
    },
    setCurrentAsDefault(clearError = true) {
      let value = this.fieldValue
      if (_.isObject(value)) value = _.clone(value)
      this.initialValue = value
      if (clearError) {
        this.status = this.error = ''
        this.markAsPristine()
        this.markAsUntouched()
      }
    },
    markAsPristine() {
      this.pristine = true
      this.dirty = false
    },
    markAsDirty() {
      this.pristine = false
      this.dirty = true
    },
    markAsUntouched() {
      this.touched = false
    },
    markAsTouched() {
      this.touched = true
    },
  },
  beforeDestroy() {
    this.dispatch('KForm', 'k.form.removeField', [this])
  },
}
